import PerformerOverview from '../Workspace/PerformerOverview';
import { Avatar } from '@mui/joy';
import { StudioModal as Modal, StudioDialog as ModalDialog } from '../Workspace/Workspace';
import { DrawingProgramsPicker } from '../DrawingPrograms';
import { formatRating } from '../../utils/rating';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  ModalClose,
  ModalOverflow,
  Stack,
  Typography,
  Select,
  Option,
  Textarea,
  Checkbox,
  Chip,
  Divider,
  Alert,
  Tab,
  TabList,
  TabPanel,
  Tabs,
  Card,
  Grid,
  LinearProgress,
  CircularProgress,
} from '@mui/joy';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip as RTooltip,
  CartesianGrid,
  ResponsiveContainer,
} from 'recharts';
import { useContext, useEffect, useRef, useState } from 'react';
import { LayoutContext } from '../../context';
import { motion, AnimatePresence } from 'framer-motion';
import {
  APICreateEmployee,
  APIUpdateEmployee,
  APIGetCitys,
  APIGetTimezones,
  APIGetEmployeeEvents,
  APIGetEmployeeDetail,
  APIGetEmployeeRatingStats,
  APIGetRegions,
  APIGetCitiesWithoutRegion,
  APICreateOrUpdateRegion,
  APIAssignCityToRegion,
  APIUnassignCityFromRegion,
  APIDeleteRegion,
} from '../../api';
import { track } from '../../utils/tracker';
import RegionManagerModal from './RegionManagerModal';
import CoverageZonesEditor from '../CoverageZonesEditor';

// Компонент для вкладки "События"
function EventsTab({ employee }) {
  const { setModalEvent, tokenEmployee } = useContext(LayoutContext);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [pagination, setPagination] = useState({
    current_page: 1,
    total_pages: 1,
    has_next: false,
    has_previous: false,
    count: 0,
  });

  // Ref для отслеживания скролла
  const observerTarget = useRef(null);

  useEffect(() => {
    if (employee && employee.id) {
      // Сбрасываем состояние при смене Исполнителя
      setEvents([]);
      setPagination({
        current_page: 1,
        total_pages: 1,
        has_next: false,
        has_previous: false,
        count: 0,
      });
      loadEmployeeEvents(1, true);
    }
  }, [employee]);

  // Настройка Intersection Observer для автоподгрузки
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && pagination.has_next && !loadingMore) {
          loadMoreEvents();
        }
      },
      { threshold: 0.1 },
    );

    if (observerTarget.current) {
      observer.observe(observerTarget.current);
    }

    return () => {
      if (observerTarget.current) {
        observer.unobserve(observerTarget.current);
      }
    };
  }, [pagination.has_next, loadingMore]);

  const loadEmployeeEvents = async (page = 1, reset = false) => {
    try {
      if (reset) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }

      const response = await APIGetEmployeeEvents(employee.id, page, 20, tokenEmployee);
      if (response && response.status === 'ok') {
        if (reset) {
          setEvents(response.results || []);
        } else {
          setEvents((prev) => [...prev, ...(response.results || [])]);
        }

        setPagination({
          current_page: response.current_page || 1,
          total_pages: response.total_pages || 1,
          has_next: response.has_next || false,
          has_previous: response.has_previous || false,
          count: response.count || 0,
        });
      }
    } catch (error) {
      console.error('Ошибка при загрузке событий:', error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const loadMoreEvents = () => {
    if (pagination.has_next && !loadingMore) {
      loadEmployeeEvents(pagination.current_page + 1);
    }
  };

  const handleEventClick = (event) => {
    setModalEvent({ event: event, open: true });
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Дата не указана';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    } catch (error) {
      return 'Неверная дата';
    }
  };

  const formatTime = (timeString) => {
    if (!timeString) return '';
    try {
      const time = new Date(`2000-01-01T${timeString}`);
      return time.toLocaleTimeString('ru-RU', {
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch (error) {
      return '';
    }
  };

  const getEventStatusColor = (state) => {
    switch (state) {
      case 'completed':
        return 'success';
      case 'drawing':
        return 'warning';
      case 'measuring_premises':
        return 'primary';
      default:
        return 'neutral';
    }
  };

  const getEventStatusText = (state) => {
    switch (state) {
      case 'completed':
        return 'Завершено';
      case 'drawing':
        return 'Черчение';
      case 'measuring_premises':
        return 'Обмер помещения';
      default:
        return state;
    }
  };

  // Компонент для отображения индикатора загрузки
  const LoadingIndicator = () => (
    <Stack spacing={1} alignItems="center" sx={{ py: 2 }}>
      <Typography level="body-sm" color="neutral">
        Загрузка...
      </Typography>
    </Stack>
  );

  // Компонент для отображения конца списка
  const EndOfListIndicator = () => (
    <Stack spacing={1} alignItems="center" sx={{ py: 2 }}>
      <Typography level="body-sm" color="neutral">
        Все события загружены
      </Typography>
      <Typography level="body-xs" color="neutral">
        Показано {events.length} из {pagination.count} событий
      </Typography>
    </Stack>
  );

  if (loading) {
    return (
      <Stack spacing={2} sx={{ py: 2 }}>
        <Typography level="body-md" textAlign="center">
          Загрузка событий...
        </Typography>
      </Stack>
    );
  }

  return (
    <>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography level="title-md">События исполнителя ({pagination.count})</Typography>
        {pagination.count > 0 && (
          <Typography level="body-sm" color="neutral">
            {events.length} из {pagination.count}
          </Typography>
        )}
      </Stack>

      <Stack spacing={2} sx={{ py: 2, maxHeight: '60vh', overflow: 'auto' }}>
        {events.length === 0 ? (
          <Alert color="neutral">У исполнителя нет событий</Alert>
        ) : (
          <>
            <Stack spacing={1}>
              {events.map((event, index) => (
                <Card
                  key={`${event.id}-${index}`}
                  variant="outlined"
                  sx={{
                    p: 2,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      backgroundColor: 'background.level1',
                      transform: 'translateY(-1px)',
                      boxShadow: 'sm',
                    },
                  }}
                  onClick={() => handleEventClick(event)}
                >
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                    <Stack spacing={1} sx={{ flex: 1 }}>
                      <Typography level="title-sm">Сделка #{event.bitrix_deal?.id || 'N/A'}</Typography>
                      <Typography level="body-sm">
                        {event.bitrix_deal?.full_address || event.bitrix_deal?.address || 'Адрес не указан'}
                      </Typography>
                      <Stack direction="row" spacing={2} flexWrap="wrap">
                        <Typography level="body-xs" color="neutral">
                          📅 {formatDate(event.date_event)}
                        </Typography>
                        {event.time_event && (
                          <Typography level="body-xs" color="neutral">
                            🕒 {formatTime(event.time_event)}
                          </Typography>
                        )}
                      </Stack>
                    </Stack>
                    <Chip size="sm" color={getEventStatusColor(event.state)} variant="soft">
                      {getEventStatusText(event.state)}
                    </Chip>
                  </Stack>

                  {event.comment && (
                    <Typography level="body-xs" sx={{ mt: 1, fontStyle: 'italic' }}>
                      💬 {event.comment}
                    </Typography>
                  )}

                  <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap">
                    {event.is_completed && (
                      <Chip size="sm" color="success" variant="outlined">
                        Завершено
                      </Chip>
                    )}
                    {event.is_deadline_missed && (
                      <Chip size="sm" color="danger" variant="outlined">
                        Дедлайн пропущен
                      </Chip>
                    )}
                  </Stack>
                </Card>
              ))}
            </Stack>

            {/* Индикатор загрузки */}
            {loadingMore && <LoadingIndicator />}

            {/* Триггер для автоподгрузки */}
            {pagination.has_next && !loadingMore && <div ref={observerTarget} style={{ height: '1px' }} />}

            {/* Индикатор конца списка */}
            {!pagination.has_next && events.length > 0 && <EndOfListIndicator />}
          </>
        )}
      </Stack>
    </>
  );
}

function RatingTab({ employee }) {
  const { tokenEmployee } = useContext(LayoutContext);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!employee?.id) return;
      setLoading(true);
      try {
        const d = await APIGetEmployeeRatingStats(employee.id, tokenEmployee);
        if (alive) setData(d && !d.isAxiosError ? d : null);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [employee?.id]);

  if (loading) {
    return (
      <Stack alignItems="center" sx={{ py: 6 }}>
        <CircularProgress />
      </Stack>
    );
  }
  if (!data || data.role_kind == null) {
    return (
      <Card variant="soft" sx={{ p: 3, textAlign: 'center', borderRadius: 'lg' }}>
        <Typography level="body-sm" color="neutral">
          Для этой роли рейтинг не рассчитывается.
        </Typography>
      </Card>
    );
  }

  const roleLabel = data.role_kind === 'quality' ? 'Оценка отдела качества' : 'Оценка от чертёжников';
  const ratingText = data.rating != null ? formatRating(data.rating) : '—';
  const history = (data.history || []).map((h) => ({ ...h, label: h.month }));
  const reviewWord = (n) => (n === 1 ? 'оценке' : 'оценкам');

  const StatChip = ({ label, value, color = 'neutral' }) => (
    <Card
      variant="soft"
      color={color}
      sx={{
        flex: 1,
        minWidth: 120,
        p: 1.5,
        textAlign: 'center',
        borderRadius: 'lg',
      }}
    >
      <Typography level="h4" sx={{ fontWeight: 800 }}>
        {value}
      </Typography>
      <Typography level="body-xs" color="neutral">
        {label}
      </Typography>
    </Card>
  );

  return (
    <Stack spacing={2.5}>
      {/* Плашка рейтинга */}
      <Card
        sx={{
          p: 3,
          borderRadius: 'xl',
          color: 'white',
          background: 'linear-gradient(135deg, #572AC3 0%, #8d6aec 100%)',
          boxShadow: '0 8px 24px rgba(87,42,195,0.35)',
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
          <Box>
            <Typography level="body-sm" sx={{ color: 'rgba(255,255,255,0.85)' }}>
              {roleLabel}
            </Typography>
            <Stack direction="row" alignItems="baseline" spacing={1}>
              <Typography
                sx={{
                  fontSize: '3rem',
                  fontWeight: 900,
                  lineHeight: 1,
                  color: 'white',
                }}
              >
                {ratingText}
              </Typography>
              <Typography sx={{ fontSize: '1.5rem', color: '#FFD700' }}>★</Typography>
            </Stack>
            <Typography level="body-xs" sx={{ color: 'rgba(255,255,255,0.85)' }}>
              по {data.reviews_count} {reviewWord(data.reviews_count)}
            </Typography>
          </Box>
          {data.is_top5 && (
            <Chip size="lg" variant="solid" color="warning" sx={{ fontWeight: 800 }}>
              🏆 Топ-{data.rank} из {data.rank_total}
            </Chip>
          )}
        </Stack>
      </Card>

      {/* Быстрые показатели */}
      <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
        <StatChip label="Событий" value={data.events_count ?? 0} />
        <StatChip label="Оценок" value={data.reviews_count ?? 0} />
        <StatChip label="В команде, дней" value={data.tenure_days ?? '—'} />
        <StatChip
          label="Место в рейтинге"
          value={data.rank ? `#${data.rank}` : '—'}
          color={data.is_top5 ? 'success' : 'neutral'}
        />
      </Stack>

      {/* Разбивка по критериям (обмерщик) */}
      {Array.isArray(data.breakdown) && data.breakdown.length > 0 && (
        <Card variant="outlined" sx={{ p: 2, borderRadius: 'lg' }}>
          <Typography level="title-sm" sx={{ mb: 1.5 }}>
            По критериям
          </Typography>
          <Stack spacing={1.25}>
            {data.breakdown.map((b) => (
              <Box key={b.label}>
                <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.25 }}>
                  <Typography level="body-sm">{b.label}</Typography>
                  <Typography level="body-sm" fontWeight="lg">
                    {Number(b.value).toFixed(2)}
                  </Typography>
                </Stack>
                <LinearProgress
                  determinate
                  value={(Number(b.value) / 5) * 100}
                  sx={{ '--LinearProgress-radius': '8px' }}
                />
              </Box>
            ))}
          </Stack>
        </Card>
      )}

      {/* График рейтинга по месяцам */}
      {history.length > 0 && (
        <Card variant="outlined" sx={{ p: 2, borderRadius: 'lg' }}>
          <Typography level="title-sm" sx={{ mb: 1.5 }}>
            Как менялся рейтинг
          </Typography>
          <Box sx={{ width: '100%', height: 220 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={history} margin={{ top: 8, right: 12, left: -18, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                <YAxis domain={[0, 5]} tick={{ fontSize: 11 }} />
                <RTooltip formatter={(v) => Number(v).toFixed(2)} labelFormatter={(l) => `Месяц: ${l}`} />
                <Line
                  type="monotone"
                  dataKey="avg"
                  stroke="#572AC3"
                  strokeWidth={2.5}
                  dot={{ r: 3 }}
                  activeDot={{ r: 5 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </Box>
        </Card>
      )}

      {/* Последние оценки */}
      {Array.isArray(data.recent_reviews) && data.recent_reviews.length > 0 && (
        <Card variant="outlined" sx={{ p: 2, borderRadius: 'lg' }}>
          <Typography level="title-sm" sx={{ mb: 1.5 }}>
            Последние оценки
          </Typography>
          <Stack spacing={1.25}>
            {data.recent_reviews.map((r, i) => (
              <Box
                key={i}
                sx={{
                  pb: 1.25,
                  borderBottom: i < data.recent_reviews.length - 1 ? '1px solid' : 'none',
                  borderColor: 'divider',
                }}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography level="body-sm" fontWeight="lg">
                    {r.author}
                  </Typography>
                  <Chip size="sm" variant="soft" color="warning">
                    ★ {formatRating(r.stars)}
                  </Chip>
                </Stack>
                {r.comment && (
                  <Typography level="body-sm" sx={{ color: 'text.secondary', mt: 0.25 }}>
                    {r.comment}
                  </Typography>
                )}
                <Typography level="body-xs" color="neutral" sx={{ mt: 0.25 }}>
                  {r.date ? new Date(r.date).toLocaleDateString('ru-RU') : ''}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Card>
      )}
    </Stack>
  );
}

function StatisticsTab({ employee }) {
  const { tokenEmployee } = useContext(LayoutContext);
  const [statistics, setStatistics] = useState([]);
  const [reviewsData, setReviewsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (employee && employee.id) {
      loadEmployeeStatistics();
    }
  }, [employee]);

  const loadEmployeeStatistics = async () => {
    try {
      setLoading(true);
      setError(null);

      // Загружаем детальную информацию о исполнителе, которая включает статистику
      const response = await APIGetEmployeeDetail(employee.id, tokenEmployee);
      if (response && response.status === 'ok') {
        // Статистика должна быть в response.employee.statistics
        const stats = response.employee?.statistics || [];
        setStatistics(stats);

        // Отзывы и рейтинг из response.employee.reviews
        const reviews = response.employee?.reviews || null;
        setReviewsData(reviews);

        if (stats.length === 0) {
          setError('Нет данных статистики для отображения');
        }
      } else {
        setError('Не удалось загрузить статистику');
      }
    } catch (error) {
      console.error('Ошибка при загрузке статистики:', error);
      setError('Ошибка при загрузке статистики');
    } finally {
      setLoading(false);
    }
  };

  const formatMonth = (monthString) => {
    try {
      const [year, month] = monthString.split('-');
      const date = new Date(year, month - 1);
      return date.toLocaleDateString('ru-RU', {
        month: 'long',
        year: 'numeric',
      });
    } catch (error) {
      return monthString;
    }
  };

  const getPerformanceColor = (eventsCount) => {
    if (eventsCount >= 15) return 'success';
    if (eventsCount >= 8) return 'warning';
    return 'neutral';
  };

  const getEfficiencyColor = (avgTime) => {
    if (avgTime <= 20) return 'success';
    if (avgTime <= 30) return 'warning';
    return 'danger';
  };

  const getDeadlineColor = (missedCount) => {
    return missedCount === 0 ? 'success' : 'danger';
  };

  if (loading) {
    return (
      <Stack spacing={2} sx={{ py: 2 }}>
        <Typography level="body-md" textAlign="center">
          Загрузка статистики...
        </Typography>
      </Stack>
    );
  }

  if (error) {
    return (
      <Stack spacing={2} sx={{ py: 2 }}>
        <Alert color="danger">{error}</Alert>
        <Button variant="outlined" onClick={loadEmployeeStatistics} loading={loading}>
          Попробовать снова
        </Button>
      </Stack>
    );
  }

  return (
    <Stack spacing={3} sx={{ py: 2 }}>
      <Typography level="title-lg">Статистика по месяцам</Typography>

      {statistics.length === 0 ? (
        <Alert color="neutral">Нет данных для отображения статистики</Alert>
      ) : (
        <>
          {/* Общая сводка */}
          <Card variant="outlined" sx={{ p: 2, backgroundColor: 'background.level1' }}>
            <Typography level="title-sm" sx={{ mb: 1 }}>
              Общая сводка
            </Typography>
            <Stack direction="row" spacing={3} justifyContent="space-around">
              <Stack alignItems="center">
                <Typography level="h4" color="primary">
                  {statistics.reduce((sum, stat) => sum + stat.events_count, 0)}
                </Typography>
                <Typography level="body-xs">Всего событий</Typography>
              </Stack>
              <Stack alignItems="center">
                <Typography level="h4" color="primary">
                  {statistics.reduce((sum, stat) => sum + stat.missed_deadlines_count, 0)}
                </Typography>
                <Typography level="body-xs">Пропущенных дедлайнов</Typography>
              </Stack>
              <Stack alignItems="center">
                <Typography level="h4" color="primary">
                  {statistics.length}
                </Typography>
                <Typography level="body-xs">Месяцев активности</Typography>
              </Stack>
            </Stack>
          </Card>

          {/* Детальная статистика по месяцам */}
          <Stack spacing={2}>
            {statistics.map((stat, index) => (
              <Card key={index} variant="outlined" sx={{ p: 2 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }}>
                  <Typography level="title-sm">{stat.month}</Typography>
                  <Chip size="sm" color={getPerformanceColor(stat.events_count)} variant="soft">
                    {stat.events_count} событий
                  </Chip>
                </Stack>

                <Stack spacing={1.5}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography level="body-sm">📊 Количество событий:</Typography>
                    </Stack>
                    <Chip size="sm" variant="soft" color={getPerformanceColor(stat.events_count)}>
                      {stat.events_count}
                    </Chip>
                  </Stack>

                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography level="body-sm">⏱️ Среднее время выполнения:</Typography>
                    </Stack>
                    <Chip size="sm" variant="soft" color={getEfficiencyColor(stat.avg_completion_time_hours)}>
                      {stat.avg_completion_time_hours?.toFixed(1) || '0'} ч
                    </Chip>
                  </Stack>

                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography level="body-sm">🚨 Пропущенные дедлайны:</Typography>
                    </Stack>
                    <Chip
                      size="sm"
                      color={getDeadlineColor(stat.missed_deadlines_count)}
                      variant={stat.missed_deadlines_count > 0 ? 'solid' : 'soft'}
                    >
                      {stat.missed_deadlines_count}
                    </Chip>
                  </Stack>
                </Stack>

                {/* Дополнительные метрики (если есть в данных) */}
                {(stat.avg_quality || stat.avg_quality_TZ || stat.avg_response) && (
                  <>
                    <Divider sx={{ my: 1.5 }} />
                    <Typography level="body-xs" color="neutral" sx={{ mb: 1 }}>
                      Дополнительные метрики:
                    </Typography>
                    <Stack direction="row" spacing={2} justifyContent="space-around">
                      {stat.avg_quality && (
                        <Stack alignItems="center">
                          <Typography level="body-sm" fontWeight="bold">
                            {formatRating(stat.avg_quality)} ⭐
                          </Typography>
                          <Typography level="body-xs">Качество</Typography>
                        </Stack>
                      )}
                      {stat.avg_quality_TZ && (
                        <Stack alignItems="center">
                          <Typography level="body-sm" fontWeight="bold">
                            {formatRating(stat.avg_quality_TZ)} ⭐
                          </Typography>
                          <Typography level="body-xs">Качество ТЗ</Typography>
                        </Stack>
                      )}
                      {stat.avg_response && (
                        <Stack alignItems="center">
                          <Typography level="body-sm" fontWeight="bold">
                            {formatRating(stat.avg_response)} ⭐
                          </Typography>
                          <Typography level="body-xs">Отклик</Typography>
                        </Stack>
                      )}
                    </Stack>
                  </>
                )}
              </Card>
            ))}
          </Stack>
        </>
      )}

      {reviewsData && (
        <>
          <Divider />

          <Typography level="title-lg">Рейтинг и отзывы</Typography>

          {/* Статистика по отзывам */}
          <Card variant="outlined" sx={{ p: 2, backgroundColor: 'background.level1' }}>
            <Stack
              direction={{ xs: 'column-reverse', sm: 'row' }}
              justifyContent="space-between"
              alignItems="center"
              sx={{ mb: 2, textAlign: { xs: 'center', sm: 'start' } }}
            >
              <Typography level="title-sm">Общая оценка исполнителя</Typography>
              <Chip size="lg" color="warning" variant="solid" sx={{ fontSize: '1.1rem', lineHeight: 1.1 }}>
                {formatRating(reviewsData.statistics.average_rating)} ⭐
              </Chip>
            </Stack>

            <Grid
              container
              spacing={2}
              columns={{
                xs: 4,
                small: 4,
                sm: 6,
                medium: 4,
                md: 3,
                large: 6,
                lg: 6,
                xl: 12,
              }}
            >
              <Grid
                xs={2}
                sm={3}
                medium={2}
                md={3}
                large={3}
                lg={3}
                xl={4}
                sx={{ width: '-webkit-fill-available' }}
              >
                <Stack alignItems="center">
                  <Typography level="h4" color="primary">
                    {reviewsData.statistics.total_reviews}
                  </Typography>
                  <Typography level="body-xs">Всего отзывов</Typography>
                </Stack>
              </Grid>
              <Grid
                xs={2}
                sm={3}
                medium={2}
                md={3}
                large={3}
                lg={3}
                xl={4}
                sx={{ width: '-webkit-fill-available' }}
              >
                <Stack alignItems="center">
                  <Typography level="h4" color="success">
                    {formatRating(reviewsData.statistics.average_quality)} ⭐
                  </Typography>
                  <Typography level="body-xs">Качество работы</Typography>
                </Stack>
              </Grid>
              <Grid
                xs={2}
                sm={3}
                medium={2}
                md={3}
                large={3}
                lg={3}
                xl={4}
                sx={{ width: '-webkit-fill-available' }}
              >
                <Stack alignItems="center">
                  <Typography level="h4" color="info">
                    {formatRating(reviewsData.statistics.average_response)} ⭐
                  </Typography>
                  <Typography level="body-xs">Отзывчивость</Typography>
                </Stack>
              </Grid>
              <Grid
                xs={2}
                sm={3}
                medium={2}
                md={3}
                large={3}
                lg={3}
                xl={4}
                sx={{ width: '-webkit-fill-available' }}
              >
                <Stack alignItems="center">
                  <Typography level="h4" color="info">
                    {formatRating(reviewsData.statistics.average_quality_TZ)} ⭐
                  </Typography>
                  <Typography level="body-xs">Качество ТЗ</Typography>
                </Stack>
              </Grid>
            </Grid>
          </Card>

          {/* Детальные отзывы */}
          {reviewsData.reviews && reviewsData.reviews.length > 0 ? (
            <Stack spacing={2}>
              <Typography level="title-sm">Последние отзывы ({reviewsData.reviews.length})</Typography>

              {reviewsData.reviews.slice(0, 5).map((review, index) => (
                <Card key={index} variant="outlined" sx={{ p: 2 }}>
                  <Stack spacing={1.5}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Stack spacing={0.5}>
                        <Stack direction={'row'} spacing={1}>
                          <Typography level="title-lg">Отзыв #{review.id}</Typography>
                          {/* <Chip component={"span"} color="warning">
                                                        {review.event_info}
                                                    </Chip> */}
                        </Stack>
                        {review.event && (
                          <Chip
                            size="sm"
                            component={'span'}
                            color={'primary'}
                            sx={{ mt: 1, width: '30vw' }}
                            noWrap
                          >
                            {review.event_address}
                          </Chip>
                        )}
                      </Stack>
                      <Chip size="md" color="warning" variant="soft">
                        {formatRating(review.stars_all)} ⭐
                      </Chip>
                    </Stack>

                    {review.comment && <Typography level="body-sm">💬 {review.comment}</Typography>}

                    <Stack direction="row" spacing={2} justifyContent="space-between">
                      <Stack spacing={0.5}>
                        <Typography level="body-sm" color="neutral">
                          Качество: {review.stars_quality} ⭐
                        </Typography>
                        <Typography level="body-sm" color="neutral">
                          Качество ТЗ: {review.stars_quality_TZ} ⭐
                        </Typography>
                      </Stack>
                      <Stack spacing={0.5}>
                        <Typography level="body-sm" color="neutral">
                          Отклик: {review.stars_response} ⭐
                        </Typography>
                        <Typography level="body-sm" color="neutral">
                          {new Date(review.date_create).toLocaleDateString('ru-RU')}
                        </Typography>
                      </Stack>
                    </Stack>

                    {review.employee_leaving && (
                      <Chip size="md" color="warning" variant="soft">
                        Оставил: {review.employee_leaving_name}
                      </Chip>
                    )}
                  </Stack>
                </Card>
              ))}

              {reviewsData.reviews.length > 5 && (
                <Alert color="neutral">Показано 5 из {reviewsData.reviews.length} отзывов</Alert>
              )}
            </Stack>
          ) : (
            <Alert color="neutral">Пока нет отзывов об этом исполнителе</Alert>
          )}
        </>
      )}
    </Stack>
  );
}

export default function ModalEmployee() {
  const {
    account,
    tokenEmployee,
    modalEmployee: modalEmployeeState,
    setModalEmployee,
    setModalEvent,
  } = useContext(LayoutContext);
  const employeeSnapshot = useRef(modalEmployeeState);
  if (modalEmployeeState.open) employeeSnapshot.current = modalEmployeeState;
  const modalEmployee = {
    ...employeeSnapshot.current,
    open: modalEmployeeState.open,
  };

  const [loading, setLoading] = useState(false);
  const [cities, setCities] = useState([]);
  const [errors, setErrors] = useState({});
  const [timezones, setTimezones] = useState([]);
  const [activeTab, setActiveTab] = useState(0);

  const [modalRegion, setModalRegion] = useState({
    open: false,
    selectedRegion: null,
    editingRegion: null,
  });
  const [regions, setRegions] = useState([]);
  const [citiesWithoutRegion, setCitiesWithoutRegion] = useState([]);
  const [loadingRegions, setLoadingRegions] = useState(false);

  const [regionsPagination, setRegionsPagination] = useState({
    current_page: 1,
    total_pages: 1,
    has_next: false,
    total_count: 0,
  });
  const [loadingMoreRegions, setLoadingMoreRegions] = useState(false);
  const observerTarget = useRef(null);

  // Данные формы
  const [formData, setFormData] = useState({
    name: '',
    last_name: '',
    patronymic: '',
    phone: '',
    email: '',
    max_link: '',
    city: '',
    city_bitrix: '',
    region: null,
    position: [],
    drawing_program_ids: [],
    role: 'employee',
    password: '',
    home_address: '',
    timezone: 'UTC',
    outsource_employee: false,
    is_block: false,
    completed_training: false,
    id_invite_konsol: null,
    id_contractor_konsol: null,
  });

  // Инициализация формы при открытии модального окна
  useEffect(() => {
    if (modalEmployee.open && modalEmployee.employee) {
      // Режим редактирования - заполняем данные исполнителя
      setFormData({
        name: modalEmployee.employee.name || '',
        last_name: modalEmployee.employee.last_name || '',
        patronymic: modalEmployee.employee.patronymic || '',
        phone: modalEmployee.employee.phone || '',
        email: modalEmployee.employee.email || '',
        max_link: modalEmployee.employee.max_link || '',
        city: modalEmployee.employee.city || '',
        city_bitrix: modalEmployee.employee.city_bitrix || '',
        region: modalEmployee.employee.region || null,
        position: modalEmployee.employee.position || [],
        drawing_program_ids: (modalEmployee.employee.drawing_programs || []).map((p) => p.id),
        role: modalEmployee.employee.role || 'employee',
        password: '', // Пароль не показываем при редактировании
        home_address: modalEmployee.employee.home_address || '',
        timezone: modalEmployee.employee.timezone || 'UTC',
        outsource_employee: modalEmployee.employee.outsource_employee || false,
        is_block: modalEmployee.employee.is_block || false,
        completed_training: modalEmployee.employee.completed_training || false,
        id_invite_konsol: modalEmployee.employee.id_invite_konsol || null,
        id_contractor_konsol: modalEmployee.employee.id_contractor_konsol || null,
      });
      // Сбрасываем активную вкладку при открытии
      setActiveTab('overview');
    } else if (modalEmployee.open) {
      // Режим создания - сбрасываем форму
      setFormData({
        name: '',
        last_name: '',
        patronymic: '',
        phone: '',
        email: '',
        max_link: '',
        city: '',
        city_bitrix: '',
        region: null,
        position: [],
        drawing_program_ids: [],
        role: 'employee',
        password: '',
        home_address: '',
        timezone: 'UTC',
        outsource_employee: false,
        is_block: false,
        completed_training: false,
        id_invite_konsol: null,
        id_contractor_konsol: null,
      });
      // В режиме создания показываем только первую вкладку
      setActiveTab(0);
    }
    setErrors({});
  }, [modalEmployee.open, modalEmployee.employee]);

  // Проверяем, является ли исполнитель обмерщиком
  const isSurveyor = formData.position.includes('surveyor');
  const isDraftsman = formData.position.includes('draftsman');
  const isEditMode = Boolean(modalEmployee.employee);

  // Загрузка городов
  useEffect(() => {
    loadCities();
    loadTimezones();
  }, []);

  // Регионы нужны, чтобы показать НАЗВАНИЕ выбранного региона в поле «Регион».
  // Грузим при первом открытии модалки.
  useEffect(() => {
    if (modalEmployee.open && regions.length === 0) {
      loadRegions(1, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalEmployee.open]);

  const loadTimezones = async () => {
    try {
      const data = await APIGetTimezones();
      if (data) {
        setTimezones(data);
      }
    } catch (error) {
      console.error('Ошибка при загрузке часовых поясов:', error);
    }
  };

  function extractErrorMessages(serverResponse) {
    // Проверяем, есть ли ошибки в ответе
    if (!serverResponse || !serverResponse.errors) {
      return [];
    }

    const errors = serverResponse.errors;
    const errorMessages = [];

    // Проходим по всем полям с ошибками
    Object.keys(errors).forEach((field) => {
      // Проверяем, является ли значение массивом (как в вашем примере)
      if (Array.isArray(errors[field])) {
        // Добавляем все сообщения из массива
        errors[field].forEach((message) => {
          errorMessages.push(message);
        });
      } else if (typeof errors[field] === 'string') {
        // Если ошибка - просто строка
        errorMessages.push(errors[field]);
      } else if (typeof errors[field] === 'object') {
        // Если вложенная структура (рекурсивный обход)
        const nestedMessages = extractErrorMessages({ errors: errors[field] });
        errorMessages.push(...nestedMessages);
      }
    });

    return errorMessages;
  }

  const loadCities = async () => {
    try {
      const data = await APIGetCitys();
      if (data) {
        setCities(data);
      }
    } catch (error) {
      console.error('Ошибка при загрузке городов:', error);
    }
  };

  // Обработчики изменений формы
  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    // Очищаем ошибку при изменении поля
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  const handlePositionToggle = (position) => {
    const newPosition = formData.position.includes(position)
      ? formData.position.filter((p) => p !== position)
      : [...formData.position, position];
    handleInputChange('position', newPosition);
  };

  // Валидация формы
  const validateForm = () => {
    const newErrors = {};

    if (!formData.name?.trim()) {
      newErrors.name = 'Имя обязательно';
    }

    if (formData.email) {
      if (!/\S+@\S+\.\S+/.test(formData.email.trim())) {
        newErrors.email = 'Некорректный email';
      }
    }

    if (!modalEmployee.employee && !formData.password) {
      newErrors.password = 'Пароль обязателен при создании';
    }

    if (formData.position.length === 0) {
      newErrors.position = 'Выберите хотя бы одну должность';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Сохранение исполнителя
  const handleSave = async () => {
    if (!validateForm()) return;

    setLoading(true);
    try {
      let result;
      if (modalEmployee.employee) {
        const dataToSend = { ...formData };
        if (!dataToSend.id_contractor_konsol) delete dataToSend.id_contractor_konsol;
        if (!dataToSend.id_invite_konsol) delete dataToSend.id_invite_konsol;

        result = await APIUpdateEmployee(modalEmployee.employee.id, dataToSend, tokenEmployee);
      } else {
        result = await APICreateEmployee(formData, tokenEmployee);
      }

      if (result.status === 'ok') {
        track(modalEmployee.employee ? 'frontend.performer.update' : 'frontend.performer.create', {
          title: modalEmployee.employee
            ? `Изменил исполнителя #${modalEmployee.employee.id}`
            : 'Создал исполнителя',
          meta: modalEmployee.employee
            ? { employee_id: modalEmployee.employee.id }
            : { employee_id: result.employee?.id },
        });
        setModalEmployee({ open: false, employee: null });
        if (modalEmployee.onSave) {
          modalEmployee.onSave(result.employee);
        }
      } else {
        // Разбираем ошибки по полям
        if (result.errors && typeof result.errors === 'object') {
          const fieldErrors = {};
          Object.keys(result.errors).forEach((field) => {
            const msgs = result.errors[field];
            if (Array.isArray(msgs)) {
              fieldErrors[field] = msgs.join(', ');
            } else if (typeof msgs === 'string') {
              fieldErrors[field] = msgs;
            } else if (typeof msgs === 'object') {
              // Вложенные ошибки (non_field_errors и т.д.)
              fieldErrors[field] = Object.values(msgs).flat().join(', ');
            }
          });

          // non_field_errors показываем в общем алерте
          if (fieldErrors.non_field_errors) {
            fieldErrors.submit = fieldErrors.non_field_errors;
            delete fieldErrors.non_field_errors;
          }

          // Если ни одного поля не нашли — показываем общую ошибку
          if (Object.keys(fieldErrors).length === 0) {
            fieldErrors.submit = result.message || 'Ошибка при сохранении';
          }

          setErrors(fieldErrors);
        } else {
          setErrors({ submit: result.message || 'Ошибка при сохранении' });
        }
      }
    } catch (error) {
      setErrors({ submit: 'Произошла ошибка при сохранении' });
    } finally {
      setLoading(false);
    }
  };

  // Опции для селектов
  const positionOptions = [
    { value: 'surveyor', label: 'Обмерщик' },
    { value: 'manager', label: 'Менеджер' },
    { value: 'draftsman', label: 'Чертежник' },
    { value: 'quality_master', label: 'Мастер качества' },
  ];

  const roleOptions = [
    { value: 'employee', label: 'Полевой исполнитель' },
    { value: 'manager', label: 'Менеджер' },
    { value: 'administrator', label: 'Администратор' },
  ];

  // Функция для загрузки регионов
  const loadRegions = async (page = 1, reset = false) => {
    try {
      if (reset) {
        setLoadingRegions(true);
        setRegions([]); // Очищаем список при сбросе
      } else {
        setLoadingMoreRegions(true);
      }

      const response = await APIGetRegions(page, 10000, tokenEmployee);
      if (response && response.regions) {
        if (reset) {
          setRegions(response.regions);
        } else {
          // Добавляем новые регионы, избегая дубликатов
          const newRegions = response.regions.filter(
            (newRegion) => !regions.some((existingRegion) => existingRegion.id === newRegion.id),
          );
          setRegions((prev) => [...prev, ...newRegions]);
        }

        // Сохраняем пагинацию
        setRegionsPagination(
          response.pagination || {
            current_page: page,
            total_pages: 1,
            has_next: false,
            total_count: 0,
          },
        );
      }
    } catch (error) {
      console.error('Ошибка при загрузке регионов:', error);
    } finally {
      setLoadingRegions(false);
      setLoadingMoreRegions(false);
    }
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (
          entries[0].isIntersecting &&
          regionsPagination.has_next &&
          !loadingMoreRegions &&
          !modalRegion.editingRegion
        ) {
          loadMoreRegions();
        }
      },
      { threshold: 0.1 },
    );

    if (observerTarget.current) {
      observer.observe(observerTarget.current);
    }

    return () => {
      if (observerTarget.current) {
        observer.unobserve(observerTarget.current);
      }
    };
  }, [regionsPagination.has_next, loadingMoreRegions, modalRegion.editingRegion]);

  const loadMoreRegions = () => {
    if (regionsPagination.has_next && !loadingMoreRegions) {
      loadRegions(regionsPagination.current_page + 1);
    }
  };

  // Функция для загрузки городов без региона
  const loadCitiesWithoutRegion = async () => {
    try {
      const response = await APIGetCitiesWithoutRegion(tokenEmployee);
      if (response && response.status === 'ok') {
        setCitiesWithoutRegion(response.cities || []);
      }
    } catch (error) {
      console.error('Ошибка при загрузке городов без региона:', error);
    }
  };

  // Обработчик открытия модального окна регионов
  const handleOpenRegionModal = () => {
    loadRegions(1, true);
    loadCitiesWithoutRegion();
    setModalRegion({
      open: true,
      selectedRegion: formData.region ? regions.find((r) => r.id === formData.region) : null,
      editingRegion: null,
    });
  };

  // Обработчик выбора региона
  const handleSelectRegion = (region) => {
    if (modalRegion.editingRegion) {
      // Если в режиме редактирования, не выбираем регион
      return;
    }

    setModalRegion((prev) => ({
      ...prev,
      selectedRegion: region,
    }));

    // Обновляем форму
    handleInputChange('region', region ? region.id : null);
  };

  // Обработчик сохранения/обновления региона
  const handleSaveRegion = async (regionData) => {
    try {
      const response = await APICreateOrUpdateRegion(regionData, tokenEmployee);
      if (response && response.status === 'ok') {
        // Перезагружаем список регионов
        await loadRegions();

        // Если это был новый регион и мы его выбрали, обновляем выбор
        if (modalRegion.selectedRegion && modalRegion.selectedRegion.id === regionData.id) {
          handleSelectRegion(response.region);
        }

        // Сбрасываем режим редактирования
        setModalRegion((prev) => ({
          ...prev,
          editingRegion: null,
        }));
      }
    } catch (error) {
      console.error('Ошибка при сохранении региона:', error);
    }
  };

  // Обработчик привязки города к региону
  const handleAssignCityToRegion = async (cityId) => {
    if (!modalRegion.editingRegion) return;

    // console.log("gggggg", cityId, modalRegion.selectedRegion, modalRegion.editingRegion)
    try {
      const response = await APIAssignCityToRegion(
        {
          city_id: cityId,
          region_id: modalRegion.editingRegion.id,
        },
        tokenEmployee,
      );

      if (response && response.status === 'ok') {
        // Обновляем редактируемый регион
        if (modalRegion.editingRegion) {
          const updatedRegion = {
            ...modalRegion.editingRegion,
            cities: [...(modalRegion.editingRegion.cities || []), response.city],
          };
          setModalRegion((prev) => ({
            ...prev,
            editingRegion: updatedRegion,
          }));
        }

        // Обновляем списки
        await loadCitiesWithoutRegion();
      }
    } catch (error) {
      console.error('Ошибка при привязке города:', error);
    }
  };

  // Обработчик отвязки города от региона
  const handleUnassignCityFromRegion = async (cityId) => {
    try {
      const response = await APIUnassignCityFromRegion(cityId, tokenEmployee);
      if (response && response.status === 'ok') {
        // Обновляем редактируемый регион
        if (modalRegion.editingRegion) {
          const updatedRegion = {
            ...modalRegion.editingRegion,
            cities: modalRegion.editingRegion.cities?.filter((city) => city.id !== cityId) || [],
          };
          setModalRegion((prev) => ({
            ...prev,
            editingRegion: updatedRegion,
          }));
        }

        // Обновляем списки
        await loadCitiesWithoutRegion();
      }
    } catch (error) {
      console.error('Ошибка при отвязке города:', error);
    }
  };

  // Обработчик удаления региона
  const handleDeleteRegion = async (regionId) => {
    try {
      const response = await APIDeleteRegion(regionId, tokenEmployee);
      if (response && response.status === 'ok') {
        // Показываем уведомление об успешном удалении
        alert(response.message || 'Регион успешно удален');

        // Если удаленный регион был выбран в форме, сбрасываем выбор
        if (formData.region === regionId) {
          handleInputChange('region', null);
        }

        // Если удаленный регион редактируется, сбрасываем режим редактирования
        if (modalRegion.editingRegion?.id === regionId) {
          setModalRegion((prev) => ({ ...prev, editingRegion: null }));
        }

        // Если удаленный регион был выбран в модалке, сбрасываем выбор
        if (modalRegion.selectedRegion?.id === regionId) {
          setModalRegion((prev) => ({ ...prev, selectedRegion: null }));
        }

        // Перезагружаем списки
        await loadRegions();
        await loadCitiesWithoutRegion();
      } else {
        alert(response.message || 'Ошибка при удалении региона');
      }
    } catch (error) {
      console.error('Ошибка при удалении региона:', error);
      alert('Произошла ошибка при удалении региона');
    }
  };

  return (
    <>
      <Modal
        sx={{ zIndex: 10013 }}
        open={modalEmployee.open}
        onClose={() => {
          if (!loading) setModalEmployee({ open: false, employee: null });
        }}
      >
        <ModalOverflow sx={{ height: 'auto' }}>
          <ModalDialog
            size="lg"
            className="studio-employee-dialog"
            sx={{
              width: { xs: 'calc(100vw - 16px)', md: 960 },
              p: 0,
              gap: 0,
              maxHeight: '92dvh',
              overflow: 'hidden',
            }}
          >
            <ModalClose
              disabled={loading}
              onClick={() => setModalEmployee({ open: false, employee: null })}
            />

            <Stack
              direction="row"
              gap={1.5}
              alignItems="center"
              sx={{ p: { xs: 2, sm: 3 }, pr: 6, flexShrink: 0 }}
            >
              <Avatar size="lg" color="primary" variant="soft">
                {formData.name?.charAt(0)}
                {formData.last_name?.charAt(0)}
              </Avatar>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  level="h3"
                  component="h2"
                  sx={{
                    fontSize: { xs: 24, sm: 28 },
                    overflowWrap: 'anywhere',
                  }}
                >
                  {modalEmployee.employee ? `${formData.name} ${formData.last_name}` : 'Создание исполнителя'}
                </Typography>
                <Stack direction="row" gap={0.75} sx={{ mt: 0.75, flexWrap: 'wrap' }}>
                  {modalEmployee.employee && (
                    <>
                      <Chip size="sm" variant="soft" color={formData.is_block ? 'warning' : 'success'}>
                        {formData.is_block
                          ? 'Заблокирован'
                          : modalEmployee.employee?.is_confirmed === false
                            ? 'На проверке'
                            : 'Активен'}
                      </Chip>
                      <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                        ID {modalEmployee.employee.id}
                      </Typography>
                    </>
                  )}
                </Stack>
              </Box>
            </Stack>

            {errors.submit && (
              <Alert color="danger" sx={{ mx: 2, mb: 2, flexShrink: 0 }}>
                {errors.submit}
              </Alert>
            )}

            <Tabs value={activeTab} onChange={(event, value) => setActiveTab(value)}>
              <TabList aria-label="Разделы исполнителя">
                {isEditMode && <Tab value="overview">Обзор</Tab>}
                <Tab value={0}>Данные</Tab>
                {isEditMode && isSurveyor && (
                  <>
                    <Tab value={1}>События</Tab>
                    <Tab value={2}>Статистика</Tab>
                    <Tab value={3}>Зоны покрытия</Tab>
                  </>
                )}
                {isEditMode && (isSurveyor || isDraftsman) && <Tab value={4}>Рейтинг</Tab>}
              </TabList>

              {isEditMode && (
                <TabPanel value="overview" sx={{ px: 0, pt: 2 }}>
                  <PerformerOverview
                    employee={modalEmployee.employee}
                    onEdit={() => setActiveTab(0)}
                    onEvents={isSurveyor ? () => setActiveTab(1) : null}
                    onReviews={isSurveyor || isDraftsman ? () => setActiveTab(4) : null}
                  />
                </TabPanel>
              )}
              {/* Вкладка "Данные" */}
              <TabPanel value={0} sx={{ px: { xs: 0, sm: 2 } }}>
                <Stack spacing={3}>
                  {/* Основная информация */}
                  <Stack spacing={2}>
                    <Typography level="title-md">Основная информация</Typography>

                    <FormControl error={!!errors.name} required>
                      <FormLabel>Имя</FormLabel>
                      <Input
                        value={formData.name}
                        onChange={(e) => handleInputChange('name', e.target.value)}
                        size="md"
                        placeholder="Введите имя исполнителя"
                      />
                      {errors.name && (
                        <Typography level="body-xs" color="danger">
                          {errors.name}
                        </Typography>
                      )}
                    </FormControl>

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                      <FormControl sx={{ flex: 1 }}>
                        <FormLabel>Фамилия</FormLabel>
                        <Input
                          value={formData.last_name}
                          onChange={(e) => handleInputChange('last_name', e.target.value)}
                          size="md"
                          placeholder="Введите фамилию"
                        />
                      </FormControl>
                      <FormControl sx={{ flex: 1 }}>
                        <FormLabel>Отчество</FormLabel>
                        <Input
                          value={formData.patronymic}
                          onChange={(e) => handleInputChange('patronymic', e.target.value)}
                          size="md"
                          placeholder="Введите отчество"
                        />
                      </FormControl>
                    </Stack>

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                      <FormControl sx={{ flex: 1 }} required error={!!errors.phone}>
                        <FormLabel>Телефон</FormLabel>
                        <Input
                          value={formData.phone}
                          onChange={(e) => handleInputChange('phone', e.target.value)}
                          size="md"
                          placeholder="79000000000"
                          color={errors.phone ? 'danger' : undefined}
                        />
                        {errors.phone && (
                          <Typography level="body-xs" color="danger">
                            {errors.phone}
                          </Typography>
                        )}
                      </FormControl>

                      <FormControl error={!!errors.email} sx={{ flex: 1 }}>
                        <FormLabel>Email</FormLabel>
                        <Input
                          type="email"
                          value={formData.email}
                          onChange={(e) => handleInputChange('email', e.target.value)}
                          size="md"
                          placeholder="email@example.com"
                        />
                        {errors.email && (
                          <Typography level="body-xs" color="danger">
                            {errors.email}
                          </Typography>
                        )}
                      </FormControl>
                    </Stack>

                    <FormControl sx={{ flex: 1 }} error={!!errors.max_link}>
                      <FormLabel>Ссылка на МАХ</FormLabel>
                      <Input
                        value={formData.max_link}
                        onChange={(e) => handleInputChange('max_link', e.target.value)}
                        size="md"
                        placeholder="https://max.ru/username или username"
                        color={errors.max_link ? 'danger' : undefined}
                      />
                      {errors.max_link && (
                        <Typography level="body-xs" color="danger">
                          {errors.max_link}
                        </Typography>
                      )}
                    </FormControl>

                    {!modalEmployee.employee && (
                      <FormControl error={!!errors.password} required>
                        <FormLabel>Пароль</FormLabel>
                        <Input
                          type="text"
                          value={formData.password}
                          onChange={(e) => handleInputChange('password', e.target.value)}
                          size="md"
                          placeholder="Введите пароль"
                          endDecorator={
                            <Button
                              variant="soft"
                              size="sm"
                              onClick={() => {
                                const chars = 'ABCDEFGHJKMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
                                let pass = '';
                                for (let i = 0; i < 6; i++) {
                                  pass += chars.charAt(Math.floor(Math.random() * chars.length));
                                }
                                handleInputChange('password', pass);
                              }}
                            >
                              Сгенерировать
                            </Button>
                          }
                        />
                        {errors.password && (
                          <Typography level="body-xs" color="danger">
                            {errors.password}
                          </Typography>
                        )}
                      </FormControl>
                    )}
                  </Stack>

                  <Divider />

                  {/* Должности и роль */}
                  <Stack spacing={2}>
                    <Chip color="primary" size="lg" sx={{ fontSize: 20 }}>
                      Должности и роль
                    </Chip>

                    <FormControl error={!!errors.position} required>
                      <FormLabel>Должности</FormLabel>
                      <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 1 }}>
                        {positionOptions.map((option) => (
                          <Chip
                            key={option.value}
                            variant={formData.position.includes(option.value) ? 'solid' : 'outlined'}
                            color="primary"
                            onClick={() => handlePositionToggle(option.value)}
                            sx={{ cursor: 'pointer' }}
                          >
                            {option.label}
                          </Chip>
                        ))}
                      </Stack>
                      {errors.position && (
                        <Typography level="body-xs" color="danger">
                          {errors.position}
                        </Typography>
                      )}
                    </FormControl>

                    <FormControl error={!!errors.role}>
                      <FormLabel>Роль в системе</FormLabel>
                      <Select
                        slotProps={{ listbox: { sx: { zIndex: 10015 } } }}
                        value={formData.role}
                        onChange={(e, value) => handleInputChange('role', value)}
                        color={errors.role ? 'danger' : undefined}
                      >
                        {roleOptions.map((option) => (
                          <Option key={option.value} value={option.value}>
                            {option.label}
                          </Option>
                        ))}
                      </Select>
                      {errors.role && (
                        <Typography level="body-xs" color="danger">
                          {errors.role}
                        </Typography>
                      )}
                    </FormControl>
                  </Stack>

                  <Divider />

                  {isDraftsman && (
                    <Stack spacing={1}>
                      <FormLabel>Программы чертёжника</FormLabel>
                      <DrawingProgramsPicker
                        value={formData.drawing_program_ids || []}
                        disabled={loading}
                        onChange={(ids) => handleInputChange('drawing_program_ids', ids)}
                      />
                      <Typography level="body-xs">
                        Если программы не выбраны, сотрудник укажет их при входе в кабинет.
                      </Typography>
                    </Stack>
                  )}

                  {/* Локация и контакты */}
                  <Stack spacing={2}>
                    <Chip color="primary" size="lg" sx={{ fontSize: 20 }}>
                      Локация и контакты
                    </Chip>

                    <Stack direction="row" spacing={2} alignItems="flex-end">
                      <FormControl sx={{ flex: 1 }}>
                        <FormLabel>Регион</FormLabel>
                        <Input
                          value={
                            formData.region ? regions.find((r) => r.id === formData.region)?.name || '' : ''
                          }
                          readOnly
                          size="md"
                          placeholder="Выберите регион"
                          endDecorator={
                            <Button variant="plain" size="sm" onClick={handleOpenRegionModal}>
                              Выбрать
                            </Button>
                          }
                        />
                      </FormControl>
                      {formData.region && (
                        <Button
                          variant="outlined"
                          size="md"
                          color="danger"
                          onClick={() => handleInputChange('region', null)}
                        >
                          Очистить
                        </Button>
                      )}
                    </Stack>

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                      <FormControl sx={{ flex: 1 }}>
                        <FormLabel>Город</FormLabel>
                        <Input
                          value={formData.city}
                          onChange={(e) => handleInputChange('city', e.target.value)}
                          size="md"
                          placeholder="Город проживания"
                        />
                      </FormControl>
                      <FormControl sx={{ flex: 1 }} required error={!!errors.city_bitrix}>
                        <FormLabel>Город (Битрикс)</FormLabel>
                        <Select
                          slotProps={{ listbox: { sx: { zIndex: 10015 } } }}
                          value={formData.city_bitrix}
                          onChange={(e, value) => handleInputChange('city_bitrix', value)}
                          color={errors.city_bitrix ? 'danger' : undefined}
                        >
                          <Option value="">Не выбран</Option>
                          {cities
                            ?.sort((a, b) => a.name.localeCompare(b.name, 'ru'))
                            ?.map((city) => (
                              <Option key={city.id} value={city.id}>
                                {city.name}
                              </Option>
                            ))}
                        </Select>
                        {errors.city_bitrix && (
                          <Typography level="body-xs" color="danger">
                            {errors.city_bitrix}
                          </Typography>
                        )}
                      </FormControl>
                    </Stack>

                    <FormControl required error={!!errors.timezone}>
                      <FormLabel>Часовой пояс</FormLabel>
                      <Select
                        slotProps={{ listbox: { sx: { zIndex: 10015 } } }}
                        value={formData.timezone}
                        onChange={(e, value) => handleInputChange('timezone', value)}
                        color={errors.timezone ? 'danger' : undefined}
                      >
                        {timezones.map((tz) => (
                          <Option key={tz[0]} value={tz[0]}>
                            {tz[1]}
                          </Option>
                        ))}
                      </Select>
                      {errors.timezone && (
                        <Typography level="body-xs" color="danger">
                          {errors.timezone}
                        </Typography>
                      )}
                    </FormControl>

                    <FormControl>
                      <FormLabel>Домашний адрес</FormLabel>
                      <Textarea
                        minRows={2}
                        value={formData.home_address}
                        onChange={(e) => handleInputChange('home_address', e.target.value)}
                        placeholder="Полный домашний адрес"
                      />
                    </FormControl>
                  </Stack>

                  <Divider />

                  {/* Налоговая информация */}
                  <Stack spacing={2}>
                    <Chip color="primary" size="lg" sx={{ fontSize: 20 }}>
                      Налоговая информация
                    </Chip>

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                      <FormControl sx={{ flex: 1 }}>
                        <FormLabel>ID Приглашения в консоль</FormLabel>
                        <Input
                          type="number"
                          value={formData.id_invite_konsol ?? ''}
                          onChange={(e) => handleInputChange('id_invite_konsol', e.target.value)}
                          size="md"
                          placeholder="12345"
                        />
                      </FormControl>
                      <FormControl sx={{ flex: 1 }}>
                        <FormLabel>ID Исполнителя в консоль</FormLabel>
                        <Input
                          type="number"
                          value={formData.id_contractor_konsol ?? ''}
                          onChange={(e) => handleInputChange('id_contractor_konsol', e.target.value)}
                          size="md"
                          placeholder="67890"
                        />
                      </FormControl>
                    </Stack>

                    <Stack direction="row" spacing={2}>
                      <FormControl>
                        <Checkbox
                          label="Outsource исполнитель"
                          checked={formData.outsource_employee}
                          onChange={(e) => handleInputChange('outsource_employee', e.target.checked)}
                        />
                      </FormControl>
                    </Stack>
                  </Stack>

                  <Divider />

                  {/* Статус и обучение */}
                  <Stack spacing={2}>
                    <Chip color="primary" size="lg" sx={{ fontSize: 20 }}>
                      Статус и обучение
                    </Chip>

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                      <FormControl>
                        <Checkbox
                          label="Заблокировать исполнителя"
                          checked={formData.is_block}
                          onChange={(e) => handleInputChange('is_block', e.target.checked)}
                        />
                      </FormControl>
                      <FormControl>
                        <Checkbox
                          label="Прошел обучение"
                          checked={formData.completed_training}
                          onChange={(e) => handleInputChange('completed_training', e.target.checked)}
                        />
                      </FormControl>
                    </Stack>
                  </Stack>

                  {/* Кнопки действий */}
                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <Button
                      variant="outlined"
                      onClick={() => setModalEmployee({ open: false, employee: null })}
                    >
                      Отмена
                    </Button>
                    <Button onClick={handleSave} loading={loading} variant="solid" color="primary">
                      {modalEmployee.employee ? 'Сохранить' : 'Создать'}
                    </Button>
                  </Stack>
                </Stack>
              </TabPanel>

              {/* Вкладка "События" */}
              {isEditMode && isSurveyor && (
                <TabPanel value={1}>
                  <EventsTab employee={modalEmployee.employee} />
                </TabPanel>
              )}

              {/* Вкладка "Статистика" */}
              {isEditMode && isSurveyor && (
                <TabPanel value={2}>
                  <StatisticsTab employee={modalEmployee.employee} />
                </TabPanel>
              )}

              {/* ── Вкладка "Зоны покрытия" ── */}
              {isEditMode && isSurveyor && (
                <TabPanel value={3}>
                  <CoverageZonesEditor
                    employeeId={modalEmployee.employee?.id}
                    token={tokenEmployee}
                    highlightRequired={true}
                    compact={false}
                  />
                </TabPanel>
              )}

              {/* ── Вкладка "Рейтинг" ── */}
              {isEditMode && (isSurveyor || isDraftsman) && (
                <TabPanel value={4}>
                  <RatingTab employee={modalEmployee.employee} />
                </TabPanel>
              )}
            </Tabs>
          </ModalDialog>
        </ModalOverflow>
      </Modal>

      {/* Модальное окно для работы с регионами */}
      <Modal
        open={modalRegion.open}
        onClose={() =>
          setModalRegion({
            open: false,
            selectedRegion: null,
            editingRegion: null,
          })
        }
        sx={{ zIndex: 10014 }}
      >
        <ModalOverflow sx={{ height: 'auto' }}>
          <ModalDialog
            size="lg"
            sx={(theme) => ({
              [theme.breakpoints.up('xl')]: {
                width: '500px',
                mx: 0,
              },
              [theme.breakpoints.down('xl')]: {
                width: '600px',
                mx: 0,
              },
              [theme.breakpoints.down('lg')]: {
                width: '600px',
                mx: 0,
              },
              [theme.breakpoints.down('md')]: {
                width: '500px',
                mx: 0,
              },
              [theme.breakpoints.down('sm')]: {
                width: '95%',
                p: 3,
              },
              width: '100%',
            })}
          >
            <ModalClose
              onClick={() =>
                setModalRegion({
                  open: false,
                  selectedRegion: null,
                  editingRegion: null,
                })
              }
            />

            <Typography level="h4" sx={{ mb: 2 }}>
              {modalRegion.editingRegion ? 'Редактирование региона' : 'Управление регионами'}
            </Typography>

            <RegionManagerModal
              regions={regions}
              selectedRegion={modalRegion.selectedRegion}
              onSelectRegion={handleSelectRegion}
              onSaveRegion={handleSaveRegion}
              citiesWithoutRegion={citiesWithoutRegion}
              onAssignCity={handleAssignCityToRegion}
              onUnassignCity={handleUnassignCityFromRegion}
              editingRegion={modalRegion.editingRegion}
              onEditRegion={(updatedRegion) => {
                // Этот колбэк теперь принимает обновленный регион
                setModalRegion((prev) => ({
                  ...prev,
                  editingRegion: updatedRegion,
                }));
              }}
              onCancelEdit={() => {
                // При отмене редактирования перезагружаем данные
                loadRegions();
                loadCitiesWithoutRegion();
                setModalRegion((prev) => ({ ...prev, editingRegion: null }));
              }}
              loading={loadingRegions}
              onClose={() =>
                setModalRegion({
                  open: false,
                  selectedRegion: null,
                  editingRegion: null,
                })
              }
              onDeleteRegion={handleDeleteRegion}
              loadingMore={loadingMoreRegions}
              observerTarget={observerTarget}
              pagination={regionsPagination}
              onLoadMore={loadMoreRegions}
            />

            <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 3 }}>
              <Button
                variant="outlined"
                onClick={() =>
                  setModalRegion({
                    open: false,
                    selectedRegion: null,
                    editingRegion: null,
                  })
                }
              >
                Закрыть
              </Button>
              <Button
                variant="solid"
                color="primary"
                onClick={() => {
                  setModalRegion({
                    open: false,
                    selectedRegion: null,
                    editingRegion: null,
                  });
                }}
              >
                Сохранить выбор
              </Button>
            </Stack>
          </ModalDialog>
        </ModalOverflow>
      </Modal>
    </>
  );
}
